import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { freeFetch, inputClass, labelClass, parseWeight, RPE_LABELS, SESSION_TYPE_LABELS } from './api';
import { GripPill } from './GripPill';
import { HealthConsent } from './HealthConsent';

const today = () => new Date().toISOString().slice(0, 10);
const FEEL_OPTIONS = ['Smooth and controlled', 'Tough but completed', 'Struggled to finish', 'Had to cut it short'];
const RPE_OPTIONS = Object.entries(RPE_LABELS).map(([value, label]) => ({ value: Number(value), label }));

const emptyForm = () => ({
  date: today(), session_type: 'heavy', load_kg: '', set1: '', set2: '', set3: '',
  rpe: '8', feel: FEEL_OPTIONS[0], discomfort: false, discomfort_note: '', notes: '',
});

export default function FreeLogSession() {
  const [form, setForm] = useState(emptyForm);
  const [grips, setGrips] = useState(['pullup', 'pullup', 'pullup']);
  const [gripTouched, setGripTouched] = useState([false, false, false]);
  const [bodyweight, setBodyweight] = useState(null);
  const [needsBodyweight, setNeedsBodyweight] = useState(false);
  const [healthConsent, setHealthConsent] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [sixModal, setSixModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    freeFetch('/me')
      .then((me) => {
        setNeedsBodyweight(me.bodyweight_kg === null || me.bodyweight_kg === undefined);
        setHealthConsent(Boolean(me.health_consent));
      })
      .catch(() => {});
  }, []);

  const onConsentChange = (granted) => {
    setHealthConsent(granted);
    if (!granted) setForm((f) => ({ ...f, discomfort_note: '' }));
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Set 1 defaults to Pull-up; any set the lifter has not touched inherits the
  // grip of the set above it.
  const effectiveGrips = grips.reduce((acc, grip, i) => {
    acc.push(i === 0 || gripTouched[i] ? grip : acc[i - 1]);
    return acc;
  }, []);

  const setGrip = (i, value) => {
    setGrips((prev) => prev.map((g, idx) => (idx === i ? value : g)));
    setGripTouched((prev) => prev.map((t, idx) => (idx === i ? true : t)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    let bwParsed = null;
    if (needsBodyweight) {
      bwParsed = parseWeight(bodyweight);
      if (Number.isNaN(bwParsed) || bwParsed < 30 || bwParsed > 250) {
        setError('Enter your bodyweight in kg (between 30 and 250).');
        return;
      }
    }
    const loadParsed = parseWeight(form.load_kg);
    if (Number.isNaN(loadParsed) || loadParsed < 0) {
      setError('Enter the weight in kg — use 0 for bodyweight only.');
      return;
    }
    if (![form.set1, form.set2, form.set3].some((v) => Number(v) > 0)) {
      setError('Log at least one set.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await freeFetch('/sessions', {
        method: 'POST',
        body: JSON.stringify({
          date: form.date,
          session_type: form.session_type,
          load_kg: loadParsed,
          ...(needsBodyweight ? { bodyweight_kg: bwParsed } : {}),
          set1_reps: Number(form.set1),
          set2_reps: Number(form.set2),
          set3_reps: Number(form.set3),
          set1_grip: effectiveGrips[0],
          set2_grip: effectiveGrips[1],
          set3_grip: effectiveGrips[2],
          rpe: Number(form.rpe),
          feel: form.feel,
          discomfort: form.discomfort,
          discomfort_note: form.discomfort && healthConsent ? form.discomfort_note : '',
          notes: form.notes,
        }),
      });
      setConfirmation(`Session logged. ${data.session_count} ${data.session_count === 1 ? 'session' : 'sessions'} total.`);
      if (data.show_six_trigger) {
        setSixModal(true);
        sessionStorage.setItem('free_six_trigger_moment', '1');
      }
      if (needsBodyweight) setNeedsBodyweight(false);
      setForm(emptyForm());
      setGrips(['pullup', 'pullup', 'pullup']);
      setGripTouched([false, false, false]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="free-log-session-page">
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-white leading-none mb-1">
        Log Session
      </h1>
      <p className="text-mono-mute font-body text-sm mb-8" data-testid="log-movement-label">Pull-ups</p>

      {confirmation && (
        <div className="border border-mono-line bg-mono-card px-4 py-3 mb-6" data-testid="free-session-confirmation">
          <p className="text-white font-body text-sm">{confirmation}</p>
        </div>
      )}

      <form onSubmit={submit} className="space-y-5" data-testid="free-session-form">
        {needsBodyweight && (
          <div data-testid="session-bodyweight-block">
            <label className={labelClass}>Your bodyweight (kg)</label>
            <input
              type="text"
              inputMode="text"
              required
              value={bodyweight ?? ''}
              onChange={(e) => setBodyweight(e.target.value)}
              className={inputClass}
              data-testid="session-bodyweight-input"
            />
            <p className="text-mono-mute text-xs font-body mt-1.5" data-testid="session-bodyweight-helper">
              Asked once. Used to calculate your strength progress. You can change this any time.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" required max={today()} value={form.date} onChange={set('date')} className={inputClass} data-testid="session-date-input" />
          </div>
          <div>
            <label className={labelClass}>Session type</label>
            <select value={form.session_type} onChange={set('session_type')} className={inputClass} data-testid="session-type-select">
              <option value="heavy">{SESSION_TYPE_LABELS.heavy}</option>
              <option value="volume">{SESSION_TYPE_LABELS.volume}</option>
              <option value="other">{SESSION_TYPE_LABELS.other}</option>
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Added weight (kg)</label>
          <input type="text" inputMode="text" required value={form.load_kg} onChange={set('load_kg')} placeholder="e.g. 20 or 20kg" className={inputClass} data-testid="session-load-input" />
        </div>

        <div>
          <div className="space-y-3">
            {['set1', 'set2', 'set3'].map((key, i) => (
              <div key={key} className="flex items-end gap-3">
                <div className="flex-1">
                  <label className={labelClass}>Set {i + 1} reps</label>
                  <input type="number" min="0" value={form[key]} onChange={set(key)} className={inputClass} data-testid={`session-${key}-input`} />
                </div>
                <GripPill
                  grip={effectiveGrips[i]}
                  onChange={(g) => setGrip(i, g)}
                  testid={`session-${key}-grip-pill`}
                />
              </div>
            ))}
          </div>
          <p className="text-mono-mute text-xs font-body mt-1.5" data-testid="session-sets-helper">
            More than 3 sets? Log your best 3. Fewer? Leave the rest blank.
          </p>
          <p className="text-mono-mute text-xs font-body mt-1" data-testid="session-grip-helper">
            Grip defaults to pull-up. Each new set copies the grip above it.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>How hard was it?</label>
            <select value={form.rpe} onChange={set('rpe')} className={inputClass} data-testid="session-rpe-select">
              {RPE_OPTIONS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
            </select>
            <p className="text-mono-mute text-xs font-body mt-1.5" data-testid="session-rpe-helper">
              How hard the set felt. Be honest — this is for you.
            </p>
          </div>
          <div>
            <label className={labelClass}>How did it feel?</label>
            <select value={form.feel} onChange={set('feel')} className={inputClass} data-testid="session-feel-select">
              {FEEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Any discomfort?</label>
          <div className="flex gap-2">
            {[false, true].map((val) => (
              <button
                key={String(val)}
                type="button"
                onClick={() => setForm((f) => ({ ...f, discomfort: val }))}
                className={`px-6 py-2 font-heading text-sm font-semibold uppercase tracking-wider rounded-none border transition-colors ${
                  form.discomfort === val
                    ? 'bg-white border-white text-black'
                    : 'border-mono-line text-mono-mute hover:border-mono-mute'
                }`}
                data-testid={`session-discomfort-${val ? 'yes' : 'no'}`}
              >
                {val ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </div>

        {form.discomfort && (
          <div data-testid="session-discomfort-note-block">
            <HealthConsent granted={healthConsent} onChange={onConsentChange} testid="session-health-consent" />
            <label className={labelClass}>Where and what kind?</label>
            <input
              type="text"
              value={form.discomfort_note}
              onChange={set('discomfort_note')}
              maxLength={300}
              disabled={!healthConsent}
              className={`${inputClass} ph-no-capture disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="session-discomfort-note-input"
            />
          </div>
        )}

        <div>
          <label className={labelClass}>Notes (optional)</label>
          <textarea value={form.notes} onChange={set('notes')} maxLength={200} rows={2} className={`${inputClass} ph-no-capture`} data-testid="session-notes-input" />
          <p className="text-mono-mute/60 text-xs font-body mt-1">{form.notes.length}/200</p>
        </div>

        {error && <p className="text-red-400 text-sm font-body" data-testid="free-session-error">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full sm:w-auto bg-white hover:bg-[#E5E5E5] disabled:opacity-50 text-black font-heading font-bold text-lg uppercase tracking-wider rounded-none px-10 py-3.5 transition-all active:scale-95"
          data-testid="session-submit-btn"
        >
          {loading ? 'Logging...' : 'Log session'}
        </button>
      </form>

      <Dialog open={sixModal} onOpenChange={setSixModal}>
        <DialogContent className="bg-mono-card border-mono-line rounded-none max-w-lg" data-testid="free-six-session-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-3xl font-black uppercase tracking-tight text-white">
              6 sessions logged.
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-white font-body leading-relaxed">
            <p>You are doing what most people only talk about.</p>
            <p>
              The full Coach Rands Method — personalised 12-week plan, weekly progression decisions, form review,
              and mindset coaching — is launching soon.
            </p>
            <p>
              You are already on the list. You will be first to know when it opens and will get priority access
              before anyone else.
            </p>
            <p className="font-semibold">Keep logging. We will be in touch.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
